import { ref, watch } from 'vue';

/**
 * @typedef {Object} UseSearchOptions
 * @property {string} [initialSearch=''] - Initial search value
 * @property {number} [debounce=300] - Debounce delay in milliseconds
 * @property {Function} [onSearchChange] - Callback function triggered when search changes
 */

/**
 * Composable for handling search functionality
 * @param {UseSearchOptions} options - Configuration options
 * @returns {Object} Search utilities
 */
export function useSearch(options = {}) {
  const search = ref(options.initialSearch || '');
  const isTyping = ref(false);

  const debounce = (fn, delay) => {
    let timeout = null;

    return (...args) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  };

  const debouncedSearch = debounce(() => {
    options.onSearchChange?.();

    isTyping.value = false;
  }, options.debounce || 300);

  // Watch for search changes
  watch(search, () => {
    isTyping.value = true;
    debouncedSearch();
  });

  return {
    search,
    debouncedSearch,
  };
}
